/* eslint-disable react/require-default-props */
import React from 'react';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import './RuleImpact.css';

type ImpactLevel = 'low' | 'medium' | 'high';

interface RuleImpactProps {
  impactLevel: ImpactLevel;
  showImpactText?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
}

/**
 * Helper function to get the appropriate icon kind for impact level
 */
const getImpactIconKind = (level: ImpactLevel): IconKind => {
  switch (level) {
    case 'high':
      return 'TrendUp';
    case 'medium':
      return 'TrendNeutral';
    case 'low':
      return 'TrendDown';
    default:
      return 'TrendUp';
  }
};

/**
 * Helper function to format impact text with proper capitalization
 */
const formatImpactText = (level: ImpactLevel): string =>
  `${level.charAt(0).toUpperCase() + level.slice(1)} Impact`;

/**
 * RuleImpact component for displaying rule impact level with icon and optional text
 *
 * @param props - The component props
 * @returns JSX element containing the rule impact indicator
 */
function RuleImpact({
  impactLevel,
  showImpactText = true,
  iconSize = 'small',
}: RuleImpactProps) {
  const iconKind = getImpactIconKind(impactLevel);
  const impactText = formatImpactText(impactLevel);

  return (
    <Box
      className={cx('Rules-RuleImpact', `Rules-RuleImpact--${impactLevel}`)}
      role='img'
      aria-label={showImpactText ? undefined : impactText}>
      <Icon
        kind={iconKind}
        size={iconSize}
        className={cx(
          'Rules-RuleImpact-icon',
          `Rules-RuleImpact-icon--${impactLevel}`
        )}
      />
      {showImpactText && (
        <Typography
          kind='body2'
          className={cx(
            'Rules-RuleImpact-text',
            `Rules-RuleImpact-text--${impactLevel}`
          )}>
          {impactText}
        </Typography>
      )}
    </Box>
  );
}

export default RuleImpact;
